import React, { useState, useRef } from "react";

const initialManualConfig = {
  type: "text",
  fieldLabel: "",
  helperText: "",
  required: true,
  notifyOnSubmission: true,
  formFields: [],
};

function ManualMetricCreator({ onCancel, onAdd }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("number");
  const [submissionType, setSubmissionType] = useState("text");
  const [captureMode, setCaptureMode] = useState("single");
  const [manualConfig, setManualConfig] = useState(initialManualConfig);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDraggingOverZone, setIsDraggingOverZone] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const draggingItemIndex = useRef(null);
  const draggingNewType = useRef(null);

  const addFormField = (type, atIndex) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      label: "Untitled question",
      helperText: "",
      required: false,
      ...(type === "dropdown" ? { options: [], optionsText: "" } : {}),
    };
    setManualConfig((prev) => {
      const newFields = [...prev.formFields];
      if (typeof atIndex === "number") newFields.splice(atIndex, 0, newField);
      else newFields.push(newField);
      return { ...prev, formFields: newFields };
    });
  };

  const updateFormField = (id, updates) => {
    setManualConfig((prev) => ({
      ...prev,
      formFields: prev.formFields.map((f) => (f.id === id ? { ...f, ...updates } : f)),
    }));
  };

  const removeFormField = (id) => {
    setManualConfig((prev) => ({ ...prev, formFields: prev.formFields.filter((f) => f.id !== id) }));
  };

  const moveFormField = (fromIndex, toIndex) => {
    setManualConfig((prev) => {
      const newFields = [...prev.formFields];
      const [movedItem] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, movedItem);
      return { ...prev, formFields: newFields };
    });
  };

  const onDragStartNew = (e, type) => {
    draggingNewType.current = type;
    draggingItemIndex.current = null;
    e.dataTransfer.setData("text/plain", type);
    e.dataTransfer.effectAllowed = "copy";
  };

  const onDragStartExisting = (e, index) => {
    draggingItemIndex.current = index;
    draggingNewType.current = null;
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOverZone = (e) => {
    e.preventDefault();
    setIsDraggingOverZone(true);
    const zone = e.currentTarget;
    const children = Array.from(zone.children).filter((c) => c.classList.contains("field-card"));
    let foundIndex = children.length;
    for (let i = 0; i < children.length; i++) {
      const rect = children[i].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height / 2) {
        foundIndex = i;
        break;
      }
    }
    setDragOverIndex(foundIndex);
  };

  const onDragLeaveZone = () => {
    setIsDraggingOverZone(false);
    setDragOverIndex(null);
  };

  const onDropInZone = (e) => {
    e.preventDefault();
    setIsDraggingOverZone(false);
    const targetIndex = dragOverIndex !== null ? dragOverIndex : manualConfig.formFields.length;
    if (draggingNewType.current) addFormField(draggingNewType.current, targetIndex);
    else if (draggingItemIndex.current !== null)
      moveFormField(draggingItemIndex.current, targetIndex > draggingItemIndex.current ? targetIndex - 1 : targetIndex);
    setDragOverIndex(null);
    draggingNewType.current = null;
    draggingItemIndex.current = null;
  };

  const handleFinish = async () => {
    if (submissionType === "form" && manualConfig.formFields.length === 0) {
      // eslint-disable-next-line no-alert -- in-app validation message
      alert("Please add at least one field to your form.");
      return;
    }
    if (
      submissionType === "form" &&
      manualConfig.formFields.some((f) => f.type === "dropdown" && (f.options || []).length === 0)
    ) {
      // eslint-disable-next-line no-alert -- in-app validation message
      alert("Add at least one option to each dropdown field.");
      return;
    }
    const metric = {
      id: `manual-${Date.now()}`,
      appId: "manual",
      name: name || "Unnamed Metric",
      description: "Manual metric updated via player submission.",
      unit,
      currentValue: 0,
      trend: 0,
      sparklineData: Array.from({ length: 10 }, (_, i) => ({ timestamp: `${i}`, value: 0 })),
    };
    const config = { ...manualConfig, type: submissionType };
    if (submissionType === "quick-capture") {
      config.requiredInputs = [{ type: "quick-capture", value: "photo", captureMode }];
    } else if (submissionType === "form" && manualConfig.formFields.length > 0) {
      const typeMap = { "short-text": "text", "long-text": "text", number: "number", date: "date", "image-upload": "image", "quick-capture": "quick-capture", dropdown: "dropdown" };
      config.requiredInputs = manualConfig.formFields.map((field, idx) => {
        const rawKey = (field.label || `field_${idx + 1}`).toLowerCase().replace(/\s+/g, "_");
        const value = field.type === "long-text" ? "description" : rawKey;
        const input = { type: typeMap[field.type] || "text", value };
        // Dropdowns carry their admin-defined choices so the manual entry
        // modal can render a pick-only select.
        if (field.type === "dropdown") input.options = field.options || [];
        return input;
      });
    } else if (submissionType === "text") {
      config.requiredInputs = [{ type: "text", value: "text_entry" }];
    } else if (submissionType === "image") {
      config.requiredInputs = [{ type: "image", value: "photo" }];
    } else if (submissionType === "link") {
      config.requiredInputs = [{ type: "link", value: "link" }];
    }
    setIsSaving(true);
    try {
      await onAdd(metric, config);
    } finally {
      setIsSaving(false);
    }
  };

  const inputBaseClass =
    "w-full bg-gray-50 border border-gray-300 px-6 py-4 rounded-2xl font-normal text-black focus:border-[var(--gmb-primary)] focus:bg-white outline-none transition-all placeholder:text-gray-600";

  const renderPreviewInput = (type, field) => {
    const c = "w-full bg-white border border-gray-200 px-4 py-3 rounded-xl text-sm text-gray-400 cursor-default pointer-events-none";
    if (type === "dropdown")
      return (
        <select className={c} disabled defaultValue="">
          <option value="">Choose an option</option>
          {(field?.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    if (type === "short-text") return <input type="text" className={c} placeholder="Short answer text" disabled />;
    if (type === "long-text") return <textarea className={c} rows={3} placeholder="Long answer text" disabled />;
    if (type === "number") return <input type="number" className={c} placeholder="0" disabled />;
    if (type === "date") return <input type="date" className={c} disabled />;
    if (type === "image-upload")
      return (
        <div className="w-full border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center gap-2 bg-white">
          <svg viewBox="0 0 24 24" className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-[10px] leading-[16px] font-semibold text-gray-400 uppercase tracking-widest">Upload image</span>
        </div>
      );
    if (type === "quick-capture")
      return (
        <div className="w-full border-2 border-dashed border-[var(--gmb-primary-tint)] rounded-xl p-6 flex flex-col items-center justify-center gap-2 bg-[var(--gmb-primary-soft)]">
          <span className="text-[22px]">📷</span>
          <span className="text-[10px] leading-[16px] font-semibold text-[var(--gmb-primary)] uppercase tracking-widest">Open camera &amp; capture</span>
          <span className="text-[10px] leading-[16px] text-gray-400">Photo + GPS address</span>
        </div>
      );
    return null;
  };

  const elements = [
    { id: "short-text", label: "Short text" },
    { id: "long-text", label: "Long text" },
    { id: "number", label: "Number" },
    { id: "date", label: "Date" },
    { id: "image-upload", label: "Image upload" },
    { id: "quick-capture", label: "Live Camera" },
    { id: "dropdown", label: "Dropdown" },
  ];

  const submissionTypes = [
    { id: "text", label: "Text", icon: "✍️" },
    { id: "image", label: "Image", icon: "📸" },
    { id: "quick-capture", label: "Live Camera", icon: "📷" },
    { id: "link", label: "Link", icon: "🔗" },
    { id: "form", label: "Form", icon: "📋" },
  ];

  if (isSaving) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center py-20 px-8 animate-in fade-in duration-300">
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 rounded-full border-4 border-gray-100 border-t-[var(--gmb-primary)] animate-spin" />
          <div className="text-center space-y-2">
            <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900">Creating your metric</h3>
            <p className="text-sm text-gray-500">Saving to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col py-12 px-8">
      <div className={`${submissionType === "form" && step === 2 ? "max-w-[1200px]" : "max-w-[800px]"} w-full mx-auto mb-10 flex items-center justify-between transition-all duration-500`}>
        <div>
          <h2 className="text-3xl leading-[38px] font-semibold text-gray-900">Create manual metric</h2>
          <p className="text-gray-500 mt-2">Set up a metric powered by user or player submissions.</p>
        </div>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-2xl text-sm font-semibold text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all">
          Cancel
        </button>
      </div>

      <div className={`${submissionType === "form" && step === 2 ? "max-w-[1200px]" : "max-w-[800px]"} w-full mx-auto transition-all duration-500`}>
        <div className="flex items-center space-x-2 mb-10 w-full max-w-sm mx-auto">
          {[1, 2].map((stepNum) => (
            <div key={stepNum} className={`h-1.5 flex-grow rounded-2xl transition-all duration-500 ${step >= stepNum ? "bg-[var(--gmb-primary)]" : "bg-gray-100"}`} />
          ))}
          <div className="pl-4 text-[10px] leading-[16px] font-semibold text-gray-400 tracking-wide whitespace-nowrap uppercase">Step 0{step} of 02</div>
        </div>

        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-white border border-gray-100 rounded-[32px] p-10 md:p-14 shadow-sm space-y-10">
              <div className="flex flex-col items-center text-center space-y-3 mb-4">
                <div className="w-16 h-16 bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] rounded-2xl flex items-center justify-center text-3xl mb-2">📋</div>
                <h3 className="text-[22px] font-semibold text-gray-900">Metric details</h3>
                <p className="text-gray-500 max-w-md">Define the name and value type for your custom metric.</p>
              </div>
              <div className="space-y-8 max-w-md mx-auto">
                <div className="space-y-2">
                  <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-widest pl-1">Metric Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales Calls Completed" className={inputBaseClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-widest pl-1">Value Type</label>
                  <select value={unit} onChange={(e) => setUnit(e.target.value)} className={inputBaseClass}>
                    <option value="number">Number (Count)</option>
                    <option value="currency">Currency (USD)</option>
                    <option value="percent">Percentage (%)</option>
                  </select>
                </div>
                <div className="pt-4">
                  <button type="button" disabled={!name} onClick={() => setStep(2)} className="w-full py-4 bg-[var(--gmb-primary)] text-white rounded-2xl font-semibold text-sm shadow-md shadow-[var(--gmb-primary-soft)] hover:bg-black transition-all disabled:opacity-30">
                    Continue setup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
            <div className="bg-white border border-gray-100 rounded-[32px] p-10 md:p-14 shadow-sm space-y-12">
              <div className="text-center space-y-3">
                <h3 className="text-[22px] font-semibold text-gray-900">Submission method</h3>
                <p className="text-gray-500">How should players or users update this metric?</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {submissionTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSubmissionType(t.id)}
                    className={`h-36 rounded-3xl border-2 transition-all flex flex-col items-center justify-center gap-4 group ${
                      submissionType === t.id ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] shadow-lg ring-4 ring-[var(--gmb-primary-soft)]" : "border-gray-50 bg-gray-50/50 hover:border-gray-100 hover:bg-white"
                    }`}
                  >
                    <div className={`text-3xl transition-transform group-hover:scale-110 ${submissionType === t.id ? "opacity-100" : "opacity-40 grayscale"}`}>{t.icon}</div>
                    <h4 className={`text-[10px] leading-[16px] font-semibold tracking-wide uppercase ${submissionType === t.id ? "text-[var(--gmb-primary)]" : "text-gray-600"}`}>{t.label}</h4>
                  </button>
                ))}
              </div>

              <div className="space-y-10 pt-10 border-t border-gray-50">
                <div className="text-center">
                  <h4 className="text-[18px] leading-[28px] font-semibold text-gray-900">Setup Fields</h4>
                  <p className="text-sm text-gray-500 mt-1">Configure the submission interface for your team.</p>
                </div>

                {submissionType === "quick-capture" && (
                  <div className="bg-gray-50/50 p-10 rounded-[24px] border border-gray-100 space-y-8">
                    <div className="text-center space-y-2">
                      <div className="w-20 h-20 bg-[var(--gmb-primary-soft)] rounded-3xl flex items-center justify-center text-4xl mx-auto">📷</div>
                      <h4 className="text-lg font-semibold text-gray-900">Camera-based capture</h4>
                      <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto">
                        Players tap the metric to open a fullscreen camera. Each photo taken is automatically submitted as one count,
                        with the player&apos;s GPS location and reverse-geocoded address saved as metadata.
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-3">
                      {["Rear camera", "GPS location", "Address lookup", "Gallery upload"].map((feat) => (
                        <span key={feat} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-600">
                          {feat}
                        </span>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      <p className="text-[10px] leading-[16px] font-semibold text-gray-400 uppercase tracking-widest text-center">Camera behavior after capture</p>
                      <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <button
                          type="button"
                          onClick={() => setCaptureMode("single")}
                          className={`flex-1 p-4 rounded-2xl border-2 text-left transition-all ${captureMode === "single" ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] ring-4 ring-[var(--gmb-primary-soft)]" : "border-gray-100 bg-white hover:border-gray-200"}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${captureMode === "single" ? "border-[var(--gmb-primary)]" : "border-gray-300"}`}>
                              {captureMode === "single" && <div className="w-2.5 h-2.5 rounded-full bg-[var(--gmb-primary)]" />}
                            </div>
                            <div>
                              <p className={`text-sm font-semibold ${captureMode === "single" ? "text-[var(--gmb-primary)]" : "text-gray-700"}`}>Close after capture</p>
                              <p className="text-[11px] leading-[16px] text-gray-400 mt-0.5">Camera closes automatically after taking a photo.</p>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCaptureMode("continuous")}
                          className={`flex-1 p-4 rounded-2xl border-2 text-left transition-all ${captureMode === "continuous" ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] ring-4 ring-[var(--gmb-primary-soft)]" : "border-gray-100 bg-white hover:border-gray-200"}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${captureMode === "continuous" ? "border-[var(--gmb-primary)]" : "border-gray-300"}`}>
                              {captureMode === "continuous" && <div className="w-2.5 h-2.5 rounded-full bg-[var(--gmb-primary)]" />}
                            </div>
                            <div>
                              <p className={`text-sm font-semibold ${captureMode === "continuous" ? "text-[var(--gmb-primary)]" : "text-gray-700"}`}>Continuous capture</p>
                              <p className="text-[11px] leading-[16px] text-gray-400 mt-0.5">Camera stays open for multiple photos in a row.</p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {submissionType === "form" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-8 flex flex-col md:flex-row gap-6">
                      <div className="w-full md:w-56 bg-gray-50 rounded-2xl p-6 border border-gray-100 space-y-2 shrink-0 h-fit">
                        <p className="text-[10px] leading-[16px] font-semibold text-gray-400 uppercase tracking-widest mb-4">Elements</p>
                        {elements.map((el) => (
                          <button
                            key={el.id}
                            type="button"
                            draggable
                            onDragStart={(e) => onDragStartNew(e, el.id)}
                            onClick={() => addFormField(el.id)}
                            className="w-full flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:border-[var(--gmb-primary)] hover:text-[var(--gmb-primary)] transition-all cursor-grab active:cursor-grabbing"
                          >
                            <span className="w-5 h-5 bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] rounded flex items-center justify-center text-[10px] leading-[16px] shadow-sm">+</span>
                            {el.label}
                          </button>
                        ))}
                      </div>
                      <div
                        className={`flex-grow rounded-[24px] border-2 border-dashed p-8 overflow-y-auto min-h-[400px] max-h-[600px] space-y-4 transition-all relative custom-scrollbar ${isDraggingOverZone ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)]" : "border-gray-200 bg-gray-50/30"}`}
                        onDragOver={onDragOverZone}
                        onDragLeave={onDragLeaveZone}
                        onDrop={onDropInZone}
                      >
                        {manualConfig.formFields.length === 0 && !isDraggingOverZone ? (
                          <div className="h-full flex flex-col items-center justify-center text-center opacity-40 py-20">
                            <div className="text-4xl mb-4">📥</div>
                            <p className="text-sm font-semibold text-gray-400">Drag elements here to build your form</p>
                          </div>
                        ) : (
                          <>
                            {manualConfig.formFields.map((field, index) => (
                              <React.Fragment key={field.id}>
                                {isDraggingOverZone && dragOverIndex === index && <div className="h-1 bg-[var(--gmb-primary)] rounded-full w-full animate-pulse shadow-[0_0_8px_rgba(124,58,237,0.5)]" />}
                                <div className="field-card bg-white border border-gray-200 rounded-2xl p-5 shadow-sm group relative hover:border-[var(--gmb-primary)]/30 transition-all">
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div draggable onDragStart={(e) => onDragStartExisting(e, index)} className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700">
                                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                                          <circle cx="9" cy="8" r="1.5" />
                                          <circle cx="9" cy="12" r="1.5" />
                                          <circle cx="9" cy="16" r="1.5" />
                                          <circle cx="15" cy="8" r="1.5" />
                                          <circle cx="15" cy="12" r="1.5" />
                                          <circle cx="15" cy="16" r="1.5" />
                                        </svg>
                                      </div>
                                      <span className="px-2 py-0.5 bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] text-[10px] leading-[16px] font-semibold rounded uppercase tracking-wider">{field.type.replace("-", " ")}</span>
                                    </div>
                                    <button type="button" onClick={() => removeFormField(field.id)} className="text-gray-400 hover:text-pink-600 transition-colors p-1" aria-label="Remove form field">
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                  {field.type === "quick-capture" ? (
                                    <div className="flex items-center gap-3 py-2 text-sm text-gray-500">
                                      <span className="text-xl">📷</span>
                                      <span className="font-medium">Opens camera with GPS &mdash; no extra fields needed</span>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                          <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-widest pl-1">Label</label>
                                          <input
                                            className="w-full text-xs font-semibold text-black bg-gray-50 border border-gray-300 p-3 rounded-xl outline-none focus:border-[var(--gmb-primary)] focus:bg-white transition-all placeholder:text-gray-600"
                                            placeholder="e.g. Untitled question"
                                            value={field.label}
                                            onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-widest pl-1">Helper Text</label>
                                          <input
                                            className="w-full text-xs text-black bg-gray-50 border border-gray-300 p-3 rounded-xl outline-none focus:border-[var(--gmb-primary)] focus:bg-white transition-all placeholder:text-gray-600"
                                            placeholder="Optional instruction"
                                            value={field.helperText}
                                            onChange={(e) => updateFormField(field.id, { helperText: e.target.value })}
                                          />
                                        </div>
                                      </div>
                                      {field.type === "dropdown" && (
                                        <div className="mt-4 space-y-1">
                                          <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-widest pl-1">Options</label>
                                          <input
                                            className="w-full text-xs text-black bg-gray-50 border border-gray-300 p-3 rounded-xl outline-none focus:border-[var(--gmb-primary)] focus:bg-white transition-all placeholder:text-gray-600"
                                            placeholder="Comma separated (e.g. Clean 365, Clean 365 VIP)"
                                            value={field.optionsText ?? (field.options || []).join(", ")}
                                            onChange={(e) =>
                                              updateFormField(field.id, {
                                                optionsText: e.target.value,
                                                options: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                                              })
                                            }
                                          />
                                          {(field.options || []).length === 0 && (
                                            <p className="text-[11px] leading-[16px] font-semibold text-pink-600 pl-1">Add at least one option for this dropdown</p>
                                          )}
                                        </div>
                                      )}
                                      <div className="mt-4 flex items-center gap-2">
                                        <button type="button" onClick={() => updateFormField(field.id, { required: !field.required })} className={`w-8 h-4.5 rounded-full relative transition-colors ${field.required ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`} aria-label={field.required ? "Required field" : "Optional field"}>
                                          <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform ${field.required ? "translate-x-4" : "translate-x-0.5"}`} />
                                        </button>
                                        <span className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-wide">Required</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </React.Fragment>
                            ))}
                            {isDraggingOverZone && dragOverIndex === manualConfig.formFields.length && <div className="h-1 bg-[var(--gmb-primary)] rounded-full w-full animate-pulse shadow-[0_0_8px_rgba(124,58,237,0.5)]" />}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="lg:col-span-4 sticky top-6">
                      <div className="bg-gray-50/50 rounded-[28px] border border-gray-100 p-8 flex flex-col min-h-[500px] shadow-inner">
                        <header className="flex items-center justify-between mb-8">
                          <span className="text-[10px] leading-[16px] font-semibold text-gray-400 uppercase tracking-[0.2em]">Preview (Player view)</span>
                          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                        </header>
                        <div className="flex-grow space-y-8 animate-in fade-in duration-300">
                          {manualConfig.formFields.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center py-12 opacity-30">
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">No fields to preview</p>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {manualConfig.formFields.map((field) => (
                                <div key={field.id} className="space-y-2">
                                  <label className="text-sm font-semibold text-gray-900 leading-tight">
                                    {field.label || "Untitled question"}
                                    {field.required && <span className="text-pink-600 ml-1 font-semibold">*</span>}
                                  </label>
                                  {field.helperText && <p className="text-[11px] leading-[16px] text-gray-500 font-medium">{field.helperText}</p>}
                                  <div className="mt-2">{renderPreviewInput(field.type, field)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="mt-12 pt-6 border-t border-gray-100">
                          <button type="button" disabled className="w-full py-4 bg-gray-100 text-gray-400 rounded-2xl font-semibold text-sm cursor-not-allowed">
                            Submit update
                          </button>
                          <p className="text-[10px] leading-[16px] text-center text-gray-300 font-medium mt-4">Actual submission is disabled in preview</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {submissionType !== "quick-capture" && submissionType !== "form" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-50/50 p-10 rounded-[24px] border border-gray-100">
                    <div className="space-y-3">
                      <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-[0.2em] pl-1">Input label (Required)</label>
                      <input
                        className={inputBaseClass}
                        placeholder={(() => { if (submissionType === "image") return "Upload receipt"; if (submissionType === "link") return "Paste link"; return "Enter amount"; })()}
                        value={manualConfig.fieldLabel}
                        onChange={(e) => setManualConfig({ ...manualConfig, fieldLabel: e.target.value })}
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] leading-[16px] font-semibold text-black uppercase tracking-[0.2em] pl-1">Helper text (Optional)</label>
                      <input className={inputBaseClass} placeholder="Briefly explain what to submit" value={manualConfig.helperText} onChange={(e) => setManualConfig({ ...manualConfig, helperText: e.target.value })} />
                    </div>
                  </div>
                )}

                {submissionType !== "quick-capture" && (
                  <div className="flex items-center justify-between p-8 bg-gray-50/50 border border-gray-100 rounded-[24px]">
                  <div>
                    <h5 className="text-[16px] leading-[24px] font-semibold text-gray-900">Require submission</h5>
                    <p className="text-[11px] leading-[16px] text-gray-500 font-medium mt-1">Players must provide this update to earn points.</p>
                  </div>
                  <button type="button" onClick={() => setManualConfig({ ...manualConfig, required: !manualConfig.required })} className={`w-12 h-7 rounded-full relative transition-colors ${manualConfig.required ? "bg-[var(--gmb-primary)]" : "bg-gray-200"}`} aria-label={manualConfig.required ? "Require submission is on" : "Require submission is off"}>
                    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${manualConfig.required ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-8">
                  <button type="button" onClick={() => setStep(1)} className="text-sm font-semibold text-gray-400 hover:text-gray-900 transition-colors uppercase tracking-widest">
                    Back to details
                  </button>
                  <button type="button" onClick={handleFinish} disabled={isSaving} className="px-14 py-4 bg-[var(--gmb-primary)] text-white rounded-[20px] font-semibold text-sm shadow-md shadow-[var(--gmb-primary-soft)] hover:bg-black transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-3">
                    {isSaving && (
                      <svg className="animate-spin w-4 h-4 text-white" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    {isSaving ? "Saving..." : "Add to dashboard"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManualMetricCreator;
